import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, GitBranch, KeyRound, LogIn, Plus, RefreshCw, Star, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { personLabel } from "../../lib/people";
import type { UserRole } from "../../types";
import { TimeAgo } from "../time-ago";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { PRIORITY_LABELS, StatusDisc, TypeIcon, useIssuesMeta } from "./meta";

function useInvalidate() {
  const queryClient = useQueryClient();
  return (...keys: string[]) => {
    for (const key of keys) void queryClient.invalidateQueries({ queryKey: [key] });
    void queryClient.invalidateQueries({ queryKey: ["issues-meta"] });
  };
}

function onApiError(error: unknown) {
  toast.error(error instanceof ApiError ? error.message : "Request failed");
}

// ------------------------------------------------------------------- users

export function UsersPanel() {
  const invalidate = useInvalidate();
  const query = useQuery({ queryKey: ["directory"], queryFn: () => api.directoryUsers(true) });
  const sync = useMutation({
    mutationFn: api.directorySync,
    onSuccess: (result) => {
      if (result.ok) {
        const slack =
          result.slack?.ok === false
            ? ""
            : result.slack?.matched
              ? `, ${result.slack.matched} Slack ${result.slack.matched === 1 ? "id" : "ids"}`
              : "";
        toast.success(`Synced: ${result.created} added, ${result.skipped} skipped${slack}`);
      } else {
        toast.error(result.error ?? "Sync failed");
      }
      if (result.slack && !result.slack.ok) toast.error(result.slack.error ?? "Slack sync failed");
      invalidate("directory");
    },
    onError: onApiError,
  });
  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.setUserActive(id, active),
    onSuccess: () => invalidate("directory"),
    onError: onApiError,
  });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) => api.setUserRole(id, role),
    onSuccess: () => invalidate("directory", "me"),
    onError: onApiError,
  });

  return (
    <Card className="gap-0 pb-0">
      <CardHeader className="border-b pb-4">
        <CardTitle className="flex items-center justify-between">
          Users
          <Button variant="outline" size="sm" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw className={sync.isPending ? "animate-spin" : ""} /> Sync from LeaseLab
          </Button>
        </CardTitle>
        <CardDescription>
          The company register. Sync adds LeaseLab staff who are not here yet and leaves existing users
          untouched, then stores a Slack id for each person whose email matches the workspace. Inactive users
          cannot be picked as reporter or reviewer, and cannot act as admins. Account shows who has signed in
          to Skynet with Microsoft. Admins can do everything. Managers can edit and route issues, sign off any
          issue in business testing, manage teams, and place issues in releases, but cannot change settings,
          ship releases, or skip review. Users can create issues, and can see teams and analytics. A user who
          is an approver on a team can sign off that team's issues in business testing. Viewers can only read
          issues and comment on them. New accounts are viewers.
        </CardDescription>
      </CardHeader>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="pl-4">Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="hidden md:table-cell">Department</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Slack</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="pr-4 text-right">Active</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(query.data?.users ?? []).map((user) => (
            <TableRow key={user.id}>
              <TableCell className="pl-4 font-medium">{personLabel(user)}</TableCell>
              <TableCell className="text-muted-foreground">{user.email}</TableCell>
              <TableCell className="hidden text-muted-foreground md:table-cell">
                {user.department ?? "—"}
              </TableCell>
              <TableCell>
                <Badge variant="outline">{user.kind === "system" ? "system" : user.source}</Badge>
              </TableCell>
              <TableCell>
                {user.kind === "system" ? (
                  <span className="text-muted-foreground">—</span>
                ) : user.hasAccount ? (
                  <Badge
                    variant="outline"
                    className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                  >
                    <LogIn /> Can sign in
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">Never signed in</span>
                )}
              </TableCell>
              <TableCell>
                {user.kind === "system" ? (
                  <span className="text-muted-foreground">—</span>
                ) : user.slackUserId ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        tabIndex={0}
                        className="cursor-default border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                      >
                        Connected
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="font-mono">{user.slackUserId}</TooltipContent>
                  </Tooltip>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Not connected
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {user.kind === "system" ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <Select
                    value={user.role}
                    onValueChange={(role) => setRole.mutate({ id: user.id, role: role as UserRole })}
                  >
                    <SelectTrigger size="sm" className="h-7 w-28" aria-label={`${personLabel(user)} role`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </TableCell>
              <TableCell className="pr-4 text-right">
                <Switch
                  checked={user.active}
                  disabled={user.kind === "system"}
                  onCheckedChange={(active) => setActive.mutate({ id: user.id, active })}
                  aria-label={`${personLabel(user)} active`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ------------------------------------------------------------------- repos

/** Slug from a display name: "Leaselab monorepo" -> "leaselab-monorepo". */
function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function ReposPanel() {
  const invalidate = useInvalidate();
  const query = useQuery({ queryKey: ["repos"], queryFn: api.repos });
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyEdited, setKeyEdited] = useState(false);
  const [url, setUrl] = useState("");
  const [branch, setBranch] = useState("main");
  const repos = query.data?.repos ?? [];
  const activeCount = repos.filter((repo) => repo.active).length;

  const create = useMutation({
    mutationFn: () =>
      api.createRepo({
        key: key.trim() || slugify(name),
        name: name.trim(),
        url: url.trim(),
        defaultBranch: branch.trim() || undefined,
      }),
    onSuccess: () => {
      setName("");
      setKey("");
      setKeyEdited(false);
      setUrl("");
      setBranch("main");
      invalidate("repos");
    },
    onError: onApiError,
  });
  const update = useMutation({
    mutationFn: ({ key, ...patch }: { key: string; url?: string; defaultBranch?: string; name?: string }) =>
      api.updateRepo(key, patch),
    onSuccess: () => invalidate("repos"),
    onError: onApiError,
  });
  const setActive = useMutation({
    mutationFn: ({ key, active }: { key: string; active: boolean }) => api.setRepoActive(key, active),
    onSuccess: () => invalidate("repos"),
    onError: onApiError,
  });
  const setDefault = useMutation({
    mutationFn: (key: string) => api.setRepoDefault(key),
    onSuccess: (result) => {
      toast.success(`${result.repo.name} is now the default repo`);
      invalidate("repos");
    },
    onError: onApiError,
  });
  const remove = useMutation({
    mutationFn: api.deleteRepo,
    onSuccess: () => invalidate("repos"),
    onError: onApiError,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Repositories
          <Button variant="outline" size="sm" asChild>
            <Link to="/repos">Browse repos</Link>
          </Button>
        </CardTitle>
        <CardDescription>
          The repositories the factory works on. Every issue is scoped to one: its stations clone that repo
          and open the pull request against its default branch, and the issues a specialist files land in the
          repo it audited. New issues take the default repo unless the reporter picks another. An inactive
          repo keeps its issues but cannot be picked for new ones or new runs; the default repo stays active.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && url.trim()) create.mutate();
          }}
        >
          <div className="min-w-40 flex-1 space-y-1.5">
            <Label htmlFor="repo-name">Name</Label>
            <Input
              id="repo-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!keyEdited) setKey(slugify(e.target.value));
              }}
              placeholder="Leaselab monorepo"
            />
          </div>
          <div className="w-36 space-y-1.5">
            <Label htmlFor="repo-key">Key</Label>
            <Input
              id="repo-key"
              value={key}
              onChange={(e) => {
                setKeyEdited(true);
                setKey(slugify(e.target.value));
              }}
              placeholder="monorepo"
              className="font-mono"
              maxLength={60}
            />
          </div>
          <div className="min-w-64 flex-[2] space-y-1.5">
            <Label htmlFor="repo-url">URL</Label>
            <Input
              id="repo-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://github.com/leaselab/monorepo"
            />
          </div>
          <div className="w-28 space-y-1.5">
            <Label htmlFor="repo-branch">Branch</Label>
            <Input
              id="repo-branch"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="font-mono"
            />
          </div>
          <Button type="submit" size="sm" disabled={!name.trim() || !url.trim() || create.isPending}>
            <Plus /> Add
          </Button>
        </form>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Repo</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Issues</TableHead>
              <TableHead>Default</TableHead>
              <TableHead className="text-right">Active</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {repos.map((repo) => (
              <TableRow key={repo.key}>
                <TableCell>
                  <Link
                    to={`/repos/${repo.key}`}
                    className="flex items-center gap-2 font-medium hover:underline"
                  >
                    <GitBranch className="size-3.5 text-muted-foreground" />
                    {repo.name}
                    <span className="font-mono text-xs font-normal text-muted-foreground">{repo.key}</span>
                  </Link>
                </TableCell>
                <TableCell>
                  <Input
                    key={repo.url}
                    defaultValue={repo.url}
                    className="h-7 min-w-44 font-mono text-xs"
                    aria-label={`${repo.name} URL`}
                    onBlur={(event) => {
                      const next = event.target.value.trim();
                      if (next && next !== repo.url) update.mutate({ key: repo.key, url: next });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    key={repo.defaultBranch}
                    defaultValue={repo.defaultBranch}
                    className="h-7 w-20 font-mono text-xs"
                    aria-label={`${repo.name} default branch`}
                    onBlur={(event) => {
                      const next = event.target.value.trim();
                      if (next && next !== repo.defaultBranch)
                        update.mutate({ key: repo.key, defaultBranch: next });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Link to={`/issues?repo=${repo.key}`} className="text-muted-foreground hover:underline">
                    {repo.issueCount}
                  </Link>
                </TableCell>
                <TableCell>
                  {repo.isDefault ? (
                    <Badge
                      variant="outline"
                      className="border-amber-500/40 text-amber-600 dark:text-amber-400"
                    >
                      <Star /> Default
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={!repo.active || setDefault.isPending}
                      onClick={() => setDefault.mutate(repo.key)}
                    >
                      Make default
                    </Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Switch
                          checked={repo.active}
                          disabled={repo.isDefault || (repo.active && activeCount <= 1)}
                          onCheckedChange={(active) => setActive.mutate({ key: repo.key, active })}
                          aria-label={`${repo.name} active`}
                        />
                      </span>
                    </TooltipTrigger>
                    {repo.isDefault && (
                      <TooltipContent>
                        The default repo stays active. Make another repo the default first.
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-destructive"
                    aria-label={`Delete ${repo.name}`}
                    disabled={repo.isDefault || repo.issueCount > 0 || remove.isPending}
                    title={
                      repo.isDefault
                        ? "The default repo cannot be deleted"
                        : repo.issueCount > 0
                          ? "Repos with issues cannot be deleted"
                          : undefined
                    }
                    onClick={() => remove.mutate(repo.key)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!query.isLoading && repos.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                  No repos yet. Add one above; the first becomes the default.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ labels

export function LabelsPanel() {
  const invalidate = useInvalidate();
  const query = useQuery({ queryKey: ["settings-labels"], queryFn: api.settingsLabels });
  const [name, setName] = useState("");
  const [color, setColor] = useState("#8C9496");
  const create = useMutation({
    mutationFn: () => api.createLabel({ name, color }),
    onSuccess: () => {
      setName("");
      invalidate("settings-labels");
    },
    onError: onApiError,
  });
  const remove = useMutation({
    mutationFn: api.deleteLabel,
    onSuccess: () => invalidate("settings-labels"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Labels</CardTitle>
        <CardDescription>Shared across every team; attach them to issues for filtering.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="label-name">Name</Label>
            <Input
              id="label-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Customer"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="label-color">Colour</Label>
            <Input
              id="label-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-16 p-1"
            />
          </div>
          <Button type="submit" size="sm" disabled={!name.trim()}>
            <Plus /> Add
          </Button>
        </form>
        <ul className="flex flex-wrap gap-2">
          {(query.data?.labels ?? []).map((label) => (
            <li
              key={label.id}
              className="group flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm"
            >
              <span className="size-2.5 rounded-full" style={{ backgroundColor: label.color }} />
              {label.name}
              <button
                type="button"
                aria-label={`Delete ${label.name}`}
                className="text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive"
                onClick={() => remove.mutate(label.id)}
              >
                <Trash2 className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------- issue types

export function TypesPanel() {
  const invalidate = useInvalidate();
  const query = useQuery({ queryKey: ["settings-types"], queryFn: api.settingsTypes });
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.createType({
        key: name.trim().toLowerCase().replace(/\s+/g, "-"),
        name: name.trim(),
        identifierPrefix: prefix || undefined,
      }),
    onSuccess: () => {
      setName("");
      setPrefix("");
      invalidate("settings-types");
    },
    onError: onApiError,
  });
  const update = useMutation({
    mutationFn: ({ key, identifierPrefix }: { key: string; identifierPrefix: string }) =>
      api.updateType(key, { identifierPrefix }),
    onSuccess: () => invalidate("settings-types"),
    onError: onApiError,
  });
  const remove = useMutation({
    mutationFn: api.deleteType,
    onSuccess: () => invalidate("settings-types"),
    onError: onApiError,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Issue types</CardTitle>
        <CardDescription>
          Each type has its own issue identifier prefix, such as BUG-1 or ENH-2.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="type-name">New type</Label>
            <Input
              id="type-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Design"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="type-prefix">Prefix</Label>
            <Input
              id="type-prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="Auto"
              className="w-24 font-mono"
              maxLength={10}
            />
          </div>
          <Button type="submit" size="sm" disabled={!name.trim()}>
            <Plus /> Add
          </Button>
        </form>
        <ul className="divide-y">
          {(query.data?.types ?? []).map((type) => (
            <li key={type.key} className="group flex items-center gap-2.5 py-2 text-sm">
              <TypeIcon typeKey={type.key} color={type.color} />
              {type.name}
              <span className="font-mono text-xs text-muted-foreground">{type.key}</span>
              <Input
                defaultValue={type.identifierPrefix}
                className="ml-auto h-7 w-20 font-mono text-xs"
                maxLength={10}
                aria-label={`${type.name} identifier prefix`}
                onBlur={(event) => {
                  const identifierPrefix = event.target.value.trim().toUpperCase();
                  if (identifierPrefix && identifierPrefix !== type.identifierPrefix)
                    update.mutate({ key: type.key, identifierPrefix });
                }}
              />
              <button
                type="button"
                aria-label={`Delete ${type.name}`}
                className="text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive"
                onClick={() => remove.mutate(type.key)}
              >
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------- statuses

export function StatusesPanel() {
  const invalidate = useInvalidate();
  const query = useQuery({ queryKey: ["settings-statuses"], queryFn: api.settingsStatuses });
  const update = useMutation({
    mutationFn: ({ key, name, color }: { key: string; name?: string; color?: string }) =>
      api.updateStatus(key, { name, color }),
    onSuccess: () => invalidate("settings-statuses"),
    onError: onApiError,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflow statuses</CardTitle>
        <CardDescription>
          Display names and colours are yours to change. The keys are fixed — the factory engine routes on
          them.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {(query.data?.statuses ?? []).map((status) => (
            <li key={status.key} className="flex items-center gap-3 py-2">
              <StatusDisc category={status.category} color={status.color} />
              <Input
                defaultValue={status.name}
                className="h-8 w-48"
                onBlur={(e) => {
                  const name = e.target.value.trim();
                  if (name && name !== status.name) update.mutate({ key: status.key, name });
                }}
              />
              <Input
                type="color"
                defaultValue={status.color}
                className="h-8 w-12 p-1"
                onBlur={(e) => {
                  if (e.target.value !== status.color)
                    update.mutate({ key: status.key, color: e.target.value });
                }}
                aria-label={`${status.name} colour`}
              />
              <span className="font-mono text-xs text-muted-foreground">{status.key}</span>
              <Badge variant="outline" className="ml-auto">
                {status.category}
              </Badge>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------ SLA policies

export function SlaPanel() {
  const invalidate = useInvalidate();
  const meta = useIssuesMeta().data;
  const query = useQuery({ queryKey: ["sla-policies"], queryFn: api.slaPolicies });
  const [name, setName] = useState("");
  const [typeKey, setTypeKey] = useState<string>("__any__");
  const [priority, setPriority] = useState<string>("__any__");
  const [sla, setSla] = useState("2d");
  const create = useMutation({
    mutationFn: () =>
      api.createSlaPolicy({
        name:
          name.trim() ||
          `${typeKey === "__any__" ? "Any type" : typeKey} / ${priority === "__any__" ? "any priority" : PRIORITY_LABELS[Number(priority)]}`,
        issueTypeKey: typeKey === "__any__" ? null : typeKey,
        priority: priority === "__any__" ? null : Number(priority),
        sla,
      }),
    onSuccess: () => {
      setName("");
      invalidate("sla-policies");
    },
    onError: onApiError,
  });
  const remove = useMutation({
    mutationFn: api.deleteSlaPolicy,
    onSuccess: () => invalidate("sla-policies"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>SLA policies</CardTitle>
        <CardDescription>
          Default SLA by type and priority on the working calendar (09:00–17:00 Mon–Fri, Sydney). The most
          specific policy wins; the Estimator station can override per issue. Tokens: 1h, 4h, or working days
          like 2d.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label>Issue type</Label>
            <Select value={typeKey} onValueChange={setTypeKey}>
              <SelectTrigger size="sm" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any type</SelectItem>
                {(meta?.types ?? []).map((type) => (
                  <SelectItem key={type.key} value={type.key}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger size="sm" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any priority</SelectItem>
                {[1, 2, 3, 4].map((value) => (
                  <SelectItem key={value} value={String(value)}>
                    {PRIORITY_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sla-token">SLA</Label>
            <Input
              id="sla-token"
              value={sla}
              onChange={(e) => setSla(e.target.value)}
              className="h-8 w-20 font-mono"
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="sla-name">Name (optional)</Label>
            <Input id="sla-name" value={name} onChange={(e) => setName(e.target.value)} className="h-8" />
          </div>
          <Button type="submit" size="sm">
            <Plus /> Add policy
          </Button>
        </form>
        <ul className="divide-y">
          {(query.data?.policies ?? []).map((policy) => (
            <li key={policy.id} className="group flex items-center gap-3 py-2 text-sm">
              <span className="font-medium">{policy.name}</span>
              <Badge variant="outline">{policy.issueTypeKey ?? "any type"}</Badge>
              <Badge variant="outline">
                {policy.priority !== null ? PRIORITY_LABELS[policy.priority] : "any priority"}
              </Badge>
              <span className="ml-auto font-mono text-xs">{policy.sla}</span>
              <button
                type="button"
                aria-label={`Delete ${policy.name}`}
                className="text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive"
                onClick={() => remove.mutate(policy.id)}
              >
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
          {query.data?.policies.length === 0 && (
            <li className="py-4 text-sm text-muted-foreground">
              No policies — new issues get no SLA until the Estimator sets one.
            </li>
          )}
        </ul>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------- API keys

export function ApiKeysPanel() {
  const invalidate = useInvalidate();
  const query = useQuery({ queryKey: ["api-keys"], queryFn: api.apiKeys });
  const [name, setName] = useState("");
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api.createApiKey(name.trim()),
    onSuccess: ({ key }) => {
      setFreshKey(key);
      setName("");
      invalidate("api-keys");
    },
    onError: onApiError,
  });
  const revoke = useMutation({
    mutationFn: api.revokeApiKey,
    onSuccess: () => invalidate("api-keys"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>API keys</CardTitle>
        <CardDescription>
          Bearer keys for the Skynet Issues MCP server and REST API — Cursor, Claude Desktop, or the admin-app
          assistant. Agent runs on the factory line get their own short-lived tokens automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="key-name">Key name</Label>
            <Input
              id="key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Admin app assistant"
            />
          </div>
          <Button type="submit" size="sm" disabled={!name.trim()}>
            <KeyRound /> Create key
          </Button>
        </form>
        {freshKey && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
            <code className="flex-1 truncate font-mono text-xs">{freshKey}</code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(freshKey);
                toast.success("Copied — store it now, it is shown once");
              }}
            >
              <Copy /> Copy
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setFreshKey(null)}>
              Done
            </Button>
          </div>
        )}
        <ul className="divide-y">
          {(query.data?.keys ?? []).map((key) => (
            <li key={key.id} className="flex items-center gap-3 py-2 text-sm">
              <KeyRound className="size-4 text-muted-foreground" />
              <span className="font-medium">{key.name}</span>
              <code className="font-mono text-xs text-muted-foreground">{key.prefix}…</code>
              {key.revokedAt ? (
                <Badge variant="outline" className="text-destructive">
                  Revoked
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {key.lastUsedAt ? (
                    <>
                      Used <TimeAgo at={key.lastUsedAt} />
                    </>
                  ) : (
                    "Never used"
                  )}
                </span>
              )}
              {!key.revokedAt && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-7 text-destructive"
                  onClick={() => revoke.mutate(key.id)}
                >
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
